///
/// EarthBrowser
///
/// Created by Matt Giger on 8/15/2025
/// Copyright (c) 2025 Geodata Labs
///

import type { EarthquakeProperties } from "@/earthbrowser/dataset-distribution/earthquakes/EarthquakesTable"
import type { AlertProperties } from "@/earthbrowser/dataset-distribution/alerts/AlertsTable"
import type { StormProperties } from "@/earthbrowser/dataset-distribution/hurricanes/HurricanesTable"
import type { WildfireProperties } from "@/earthbrowser/dataset-distribution/wildfires/WildfiresTable"
import type { CurrentWeather } from "@/earthbrowser/dataset-distribution/weather-grid/WeatherCompute"
import { createSlice, type PayloadAction } from "@reduxjs/toolkit"
import type { Placemark } from "./placemarksSlice"
import type { BuoyEntry } from "@shared-types/buoy"
import type { LocationInfo } from "@shared-types/location-info"
import type { WebcamEntry } from "@shared-types/webcam"
import type { SatelliteEntry } from "@shared-types/satellite-entry"
import { type VolcanoInfo } from "@shared-types/volcano-info"
import type { DatasetType } from "@/types/DatasetElement"
import { type WindowType } from "./dialogSlice"

// Persisted window state for restoration on launch
export interface PersistedWindowState {
	id: string
	x: number
	y: number
	width: number
	height: number
	isMaximized: boolean
	isVisible: boolean
	title: string
	windowType?: WindowType
	// Data references for restoration
	dataSourceId?: string
	catalogId?: string
	activeLayerName?: string
	// UI options
	status?: string // Optional status text below the title
	// Dock mode - determines if window can be docked, windowed, or both (default: 'both')
	dockMode?: "window-only" | "dock-only" | "both"
	// Whether this window was docked in the bottom drawer when saved
	isDockedInDrawer?: boolean
}

// Info window theme types - matches design system themes
export type InfoWindowTheme = "location" | "satellite" | "volcano" | "earthquake" | "buoy" | "webcam" | "radar" | "wildfire" | "hurricane" | "alert" | "placemark" | "synoptic" | "default"

// Info window content types - discriminated union for type-safe content
export type InfoWindowContentType = "location" | "satellite" | "volcano" | "earthquake" | "buoy" | "webcam" | "radar" | "wildfire" | "hurricane" | "alert" | "placemark" | "pressure_center"

/** Synoptic pressure centre summary for the hover callout */
export interface PressureCenterInfo {
	centerType: "H" | "L"
	lat: number
	lon: number
	pressureHpa: number
	/** 3 h pressure change, NaN when the centre has no history */
	tendency3h: number
	/** Motion from the track (km/h and compass bearing), undefined when untracked */
	motion?: { speedKmh: number; bearingDeg: number }
	/** Extreme central pressure along the track and when it occurs */
	extreme?: { pressureHpa: number; timeMs: number }
}

/**
 * Cross-dataset context around a city, shown in the location hover callout:
 * nearby webcams, coastal buoy water temperature, and aurora chance
 */
export interface LocationNearbyInfo {
	webcamCount: number
	nearestWebcamName: string | null
	nearestWebcamKm: number | null
	buoyName: string | null
	buoyWaterTempC: number | null
	buoyKm: number | null
	auroraChancePct: number | null
	isNight: boolean
}

export type InfoWindowContentData =
	| { type: "location"; locationInfo: LocationInfo; countryName: string | null; weather?: CurrentWeather | null; nearby?: LocationNearbyInfo | null }
	| { type: "satellite"; satellite: SatelliteEntry; livePosition?: { lat: number; lon: number; alt: number }; liveSpeed?: number }
	| { type: "volcano"; volcano: VolcanoInfo }
	| { type: "earthquake"; earthquake: EarthquakeProperties }
	| { type: "buoy"; buoy: BuoyEntry }
	| { type: "webcam"; webcam: WebcamEntry }
	| { type: "radar"; stationId: string; name: string }
	| { type: "wildfire"; wildfire: WildfireProperties }
	| { type: "hurricane"; storm: StormProperties }
	| { type: "alert"; alert: AlertProperties }
	| { type: "placemark"; placemark: Placemark; memberId?: string }
	| { type: "pressure_center"; center: PressureCenterInfo }

export interface InfoWindowState {
	isOpen: boolean
	position: { x: number; y: number }
	size: { width: number; height: number }
	title: string
	subtitle?: string
	theme: InfoWindowTheme
	contentType: InfoWindowContentType | null
	contentData: InfoWindowContentData | null
}

// Recently-used layers panel commands, persisted so the section is useful
// at the start of a session. Serializable snapshot only: icons and actions
// are rebuilt from the id/type at render time (LayersPanel).
export type RecentCommandType = "tool" | "location" | "satellite" | "action" | "earthquake" | "volcano" | "setting" | "view" | "placemark"

export interface RecentCommandEntry {
	/** CommandItem id, e.g. "toggle-clouds" or "location-5391959" */
	id: string
	type: RecentCommandType
	title: string
	subtitle?: string
}

export const cMaxRecentCommands = 5

// Fits the common five-column tables (earthquakes, volcanoes) without a
// horizontal scroll at their default column widths
export const cDefaultDockWidth = 520

// Persisted data dock state (the key keeps its original "bottomDrawer"
// name; desktop docks on the right, phones use the bottom sheet)
export interface PersistedBottomDrawerState {
	// Phone sheet height
	height: number
	isOpen: boolean
	dockedWindowIds: string[] // Window IDs that were docked when last saved
	// Window ID of the tab that was selected when last saved
	activeTabId: string | null
	// Desktop right dock panel width
	dockWidth: number
}

// Layer legend tray (persisted). A legend shows while its layer is visible
// as either a full card (expanded) or a collapsed pill (chip); datasets
// without an entry render as chips. Hiding is a property of the whole
// tray, not of a legend, and is session-only (dialog.legendTrayHidden)
export type LegendDisplayState = "expanded" | "chip"

export interface LegendsState {
	display: Partial<Record<DatasetType, LegendDisplayState>>
	// The legend the tray opened on its own for the most recently enabled
	// layer. Enabling the next layer demotes it to a chip so the tray never
	// stacks cards by itself; legends the user expanded stay open
	autoExpanded: DatasetType | null
}

export const defaultLegendsState: LegendsState = {
	display: {},
	autoExpanded: null,
}

// Layers panel catalog preferences (persisted). Group ids are the
// LayerGroup keys from layers-panel/layerRegistry; unknown ids are
// ignored by the panel so a renamed group just reopens
export interface LayersPanelState {
	collapsedGroups: string[]
}

export const defaultLayersPanelState: LayersPanelState = {
	collapsedGroups: [],
}

interface UserInterfaceState {
	infoWindow: InfoWindowState
	// Persisted window states for restoration on launch (keyed by window ID)
	persistedWindowStates: Record<string, PersistedWindowState>
	// Bottom drawer state (persisted)
	bottomDrawer: PersistedBottomDrawerState
	// Recently-used layers panel commands, most recent first (persisted)
	recentCommands: RecentCommandEntry[]
	// Layer legend tray state (persisted)
	legends: LegendsState
	// Layers panel group collapse state (persisted)
	layersPanel: LayersPanelState
}

const defaultInfoWindowState: InfoWindowState = {
	isOpen: false,
	position: { x: 100, y: 100 },
	size: { width: 320, height: 280 },
	title: "",
	subtitle: undefined,
	theme: "default",
	contentType: null,
	contentData: null
}

const initialState: UserInterfaceState = {
	infoWindow: defaultInfoWindowState,
	persistedWindowStates: {},
	bottomDrawer: {
		height: 400,
		isOpen: true,
		dockedWindowIds: [],
		activeTabId: null,
		dockWidth: cDefaultDockWidth,
	},
	recentCommands: [],
	legends: defaultLegendsState,
	layersPanel: defaultLayersPanelState,
}

// Helper to ensure infoWindow state exists
function ensureInfoWindow(state: UserInterfaceState): InfoWindowState {
	if (!state.infoWindow) {
		state.infoWindow = { ...defaultInfoWindowState }
	}
	return state.infoWindow
}

function ensureLegends(state: UserInterfaceState): LegendsState {
	if (!state.legends) {
		state.legends = { display: {}, autoExpanded: null }
	}
	return state.legends
}

export const userInterfaceSlice = createSlice({
	name: "userInterface",
	initialState,
	reducers: {
		// New generic info window actions
		openInfoWindow: (state, action: PayloadAction<{
			title: string
			subtitle?: string
			theme?: InfoWindowTheme
			contentType: InfoWindowContentType
			contentData: InfoWindowContentData
			size?: { width: number; height: number }
			position?: { x: number; y: number }
		}>) => {
			const infoWindow = ensureInfoWindow(state)
			infoWindow.isOpen = true
			infoWindow.title = action.payload.title
			infoWindow.subtitle = action.payload.subtitle
			// Content types without a theme of their own (pressure_center) fall back to default
			const contentTheme = action.payload.contentType === "pressure_center" ? "synoptic" : action.payload.contentType
			infoWindow.theme = action.payload.theme ?? contentTheme ?? "default"
			infoWindow.contentType = action.payload.contentType
			infoWindow.contentData = action.payload.contentData
			if (action.payload.size) {
				infoWindow.size = action.payload.size
			}
			if (action.payload.position) {
				infoWindow.position = action.payload.position
			}
		},
		closeInfoWindow: (state) => {
			const infoWindow = ensureInfoWindow(state)
			infoWindow.isOpen = false
			infoWindow.contentType = null
			infoWindow.contentData = null
			infoWindow.title = ""
			infoWindow.subtitle = undefined
			infoWindow.theme = "default"
		},
		updateSatellitePosition: (state, action: PayloadAction<{ lat: number; lon: number; alt: number }>) => {
			const infoWindow = ensureInfoWindow(state)
			if (infoWindow.contentType === "satellite" && infoWindow.contentData?.type === "satellite") {
				infoWindow.contentData.livePosition = action.payload
			}
		},

		// Persisted window states
		saveWindowState: (state, action: PayloadAction<PersistedWindowState>) => {
			const windowState = action.payload
			state.persistedWindowStates[windowState.id] = windowState
		},

		removeWindowState: (state, action: PayloadAction<string>) => {
			const windowId = action.payload
			delete state.persistedWindowStates[windowId]
		},

		clearWindowStates: (state) => {
			state.persistedWindowStates = {}
		},

		// Bottom drawer persistence
		setPersistedBottomDrawerHeight: (state, action: PayloadAction<number>) => {
			state.bottomDrawer.height = action.payload
		},

		setPersistedBottomDrawerIsOpen: (state, action: PayloadAction<boolean>) => {
			state.bottomDrawer.isOpen = action.payload
		},

		setPersistedBottomDrawerDockedWindows: (state, action: PayloadAction<string[]>) => {
			state.bottomDrawer.dockedWindowIds = action.payload
		},

		setPersistedBottomDrawerActiveTab: (state, action: PayloadAction<string | null>) => {
			state.bottomDrawer.activeTabId = action.payload
		},

		setPersistedDockWidth: (state, action: PayloadAction<number>) => {
			state.bottomDrawer.dockWidth = action.payload
		},

		addDockedWindowId: (state, action: PayloadAction<string>) => {
			if (!state.bottomDrawer.dockedWindowIds.includes(action.payload)) {
				state.bottomDrawer.dockedWindowIds.push(action.payload)
			}
		},

		removeDockedWindowId: (state, action: PayloadAction<string>) => {
			state.bottomDrawer.dockedWindowIds = state.bottomDrawer.dockedWindowIds.filter(
				id => id !== action.payload
			)
		},

		// Recently-used layers panel commands: dedupe by id, newest first
		recordRecentCommand: (state, action: PayloadAction<RecentCommandEntry>) => {
			const existing = state.recentCommands ?? []
			state.recentCommands = [
				action.payload,
				...existing.filter(entry => entry.id !== action.payload.id),
			].slice(0, cMaxRecentCommands)
		},

		// Legend tray: a layer with a legend was just enabled. Open its
		// legend and collapse the one the tray opened last time, so enabling
		// layers never stacks cards
		legendLayerEnabled: (state, action: PayloadAction<DatasetType>) => {
			const legends = ensureLegends(state)
			const type = action.payload
			const previous = legends.autoExpanded
			if (previous && previous !== type && legends.display[previous] === "expanded") {
				legends.display[previous] = "chip"
			}
			legends.display[type] = "expanded"
			legends.autoExpanded = type
		},

		// Explicit user choice; an auto-opened legend the user touches
		// becomes theirs and is no longer demoted automatically
		setLegendDisplay: (state, action: PayloadAction<{ type: DatasetType; display: LegendDisplayState }>) => {
			const legends = ensureLegends(state)
			const { type, display } = action.payload
			legends.display[type] = display
			if (legends.autoExpanded === type) {
				legends.autoExpanded = null
			}
		},
		// Layers panel: fold a catalog group to just its header
		setLayerGroupCollapsed: (state, action: PayloadAction<{ group: string; collapsed: boolean }>) => {
			if (!state.layersPanel) {
				state.layersPanel = { collapsedGroups: [] }
			}
			const { group, collapsed } = action.payload
			const without = state.layersPanel.collapsedGroups.filter(g => g !== group)
			state.layersPanel.collapsedGroups = collapsed ? [...without, group] : without
		},
	}
})

export const {
	// New info window actions
	openInfoWindow,
	closeInfoWindow,
	updateSatellitePosition,
	// Persisted window states
	saveWindowState,
	removeWindowState,
	clearWindowStates,
	// Bottom drawer persistence
	setPersistedBottomDrawerHeight,
	setPersistedBottomDrawerIsOpen,
	setPersistedBottomDrawerDockedWindows,
	setPersistedBottomDrawerActiveTab,
	setPersistedDockWidth,
	addDockedWindowId,
	removeDockedWindowId,
	// Recently-used commands
	recordRecentCommand,
	// Legend tray
	legendLayerEnabled,
	setLegendDisplay,
	// Layers panel
	setLayerGroupCollapsed,
} = userInterfaceSlice.actions
